import { el, mount, unmount } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import externalEventCodes from 'event-system/codes/external';

import * as meetingStatuses from 'constants/meeting-statuses';
import * as meetingLiveStatuses from 'constants/meeting-live-statuses';

import VideoHorizontalFlipProcessor from './video-horizontal-flip-processor';
import AudioVideoObserver from './audio-video-observer';
import AudioControls from '../audio-controls';
import MeetingAudioControls from '../meeting-audio-controls';
import VideoControls from '../video-controls';
import ScreenPresenterControls from './screen-presenter-controls';
import AttendeesControl from './attendees-control';
import LeaveMeetingControl from './leave-meeting-control';
import ElementResizeControl from './element-resize-control';
import Stage from './stage';
import HostOfflineView from './host-offline-view';

class MeetingRoom {

  constructor(meetingOwner, meeting, attendee, meetingSession, controlOptions, options) {

    this.currentUserId = application.getUser()._id;
    this.meetingOwner = meetingOwner;
    this.meeting = meeting;
    this.isOwner = this.currentUserId === this.meeting.ownerId;
    this.attendee = attendee;
    this.attendees = [];
    this.attendeeIds = [];
    this.meetingSession = meetingSession;
    this.controlOptions = controlOptions;
    this.videoResolution = {};
    this.audioInputConnected = false;
    this.videoInputConnected = false;
    this.outputAudio = options.outputAudio;

    this.spinner = null;
    this.fullscreenMode = false;
    this.streamingDisplay = false;
    this.startDisplayStreamButton = null;
    this.stopDisplayStreamButton = null;
    this.screenPresenterControls = null;
    this.meetingAudioControls = null;
    this.audioControls = null;
    this.videoControls = null;
    this.hostOfflineView = null;
    this.stage = null;
    this.meetingRecorder = null;
    this.ownerVideoTileId = null;
    this.ownerScreenVideoTileId = null;

    this.elementResizeControl = new ElementResizeControl();

    this.userProfileDisplay = el('.user-profile-display', {
      style: {
        backgroundImage: `url(${meetingOwner.profilePicture.url})`
      }
    });
    this.meetingRoomProducerCameraDisplay = el('video.meeting-room-producer-camera-display', { style: { position: 'fixed', top: 0, left: 0, zIndex: -1, opacity: 0 } });
    this.meetingRoomProducerScreenDisplay = el('video.meeting-room-producer-screen-display', { style: { position: 'fixed', top: 0, left: 0, zIndex: -1, opacity: 0 } });
    this.meetingRoomDisplaysContainer = el('.meeting-room-displays-container', [this.meetingRoomProducerCameraDisplay, this.meetingRoomProducerScreenDisplay]);
    this.meetingRoomStageContainer = el('.meeting-room-stage-container');
    this.meetingRoomVideoContentContainer = el('.meeting-room-video-content-container', [this.meetingRoomDisplaysContainer, this.meetingRoomStageContainer]);
    this.elementResizeControlContainer = el('.meeting-room-screen-resize-control-container', this.elementResizeControl);

    this.meetingRoomControls = el('.meeting-room-controls');
    this.meetingRoomVideoSection = el('.meeting-room-video-section', [this.meetingRoomVideoContentContainer, this.meetingRoomControls, this.elementResizeControlContainer]);
    this.meetinRoomWrapper = el('.meeting-room-wrapper', [this.meetingRoomVideoSection]);
    this.speaker = el('audio.meeting-room-speaker');
    this.el = el('.meeting-room-main', [this.speaker, this.meetinRoomWrapper]);

    this.audioVideoObserver = null;
    this.boundHandleDoubleClickEvent = this.handleDoubleClickEvent.bind(this);
    this.boundHandleFullscreenOn = this.handleFullscreenOn.bind(this);
    this.boundHandleFullscreenOff = this.handleFullscreenOff.bind(this);
    this.boundHandleMeetingHostVideoSettingsChangedEvent = null;
    this.boundHandleAudioMuted = this.handleAudioMuted.bind(this);
    this.boundHandleAudioUnmuted = this.handleAudioUnmuted.bind(this);
    this.boundHandleCameraStarted = this.handleCameraStarted.bind(this);
    this.boundHandleCameraStopped = this.handleCameraStopped.bind(this);

  }

  onmount() {

    this.initialize();
    this.registerEventHandlers();

    this.mountSpinner();

  }

  onunmount() {

    this.el.removeEventListener('dblclick', this.boundHandleDoubleClickEvent);
    this.el.removeEventListener('fullscreen-on', this.boundHandleFullscreenOn);
    this.el.removeEventListener('fullscreen-off', this.boundHandleFullscreenOff);

    if (this.audioControls && this.audioControls.el) {
      this.audioControls.el.removeEventListener('muted', this.boundHandleAudioMuted);
      this.audioControls.el.removeEventListener('unmuted', this.boundHandleAudioUnmuted);
    }

    if (this.videoControls && this.videoControls.el) {
      this.videoControls.el.removeEventListener('camera-stream-started', this.boundHandleCameraStarted);
      this.videoControls.el.removeEventListener('camera-stream-stopped', this.boundHandleCameraStopped);
    }

    application.eventSystem.internal.offAllOn(this);

    if (this.audioVideoObserver !== null && this.meetingSession && this.meetingSession.audioVideo) {
      this.meetingSession.audioVideo.removeObserver(this.audioVideoObserver);
      this.audioVideoObserver = null;
    }

    if (this.meetingRoomProducerCameraDisplay) {
      this.meetingRoomProducerCameraDisplay.srcObject = null;
      this.meetingRoomProducerCameraDisplay.pause();
    }

    if (this.meetingRoomProducerScreenDisplay) {
      this.meetingRoomProducerScreenDisplay.srcObject = null;
      this.meetingRoomProducerScreenDisplay.pause();
    }

    try {
      unmount(this.meetingRoomStageContainer, this.stage);
    } catch (exception) {}

    this.unmountControls();

    this.stage = null;
    this.meetingSession = null;
    this.el = null;

  }

  update(meeting) {

    this.meeting = meeting;

    this.tryShowOfflineView();

  }

  tryShowOfflineView() {

    if (this.meeting.status !== meetingStatuses.LIVE) {
      return;
    }

    if (this.meeting.liveStatus !== meetingLiveStatuses.CONNECTED) {
      this.displayHostOfflineView();
    } else {
      this.hideHostOfflineView();
    }

  }

  initialize() {

    this.stage = new Stage(this.meeting, this.meetingRoomProducerCameraDisplay, this.meetingRoomProducerScreenDisplay, this.el.offsetWidth, this.el.offsetHeight || window.innerHeight, this.meetingRoomStageContainer);
    this.elementResizeControl.setTarget(this.el);

    this.audioVideoObserver = new AudioVideoObserver(this);
    this.meetingSession.audioVideo.addObserver(this.audioVideoObserver);
    this.meetingSession.audioVideo.start();
    this.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(this.subscribeToAttendeeIdPresenceCallback.bind(this));
    this.tryStartLocalVideoTile();

    this.tryBindAudioOutput();
    this.initializeControls();

  }

  initializeControls() {

    const meetingRoom = this;
    const meeting = meetingRoom.meeting;
    const controlOptions = this.controlOptions;

    if (controlOptions.audio === true) {

      this.audioControls = new AudioControls(true, this.currentUserId, meeting);

      this.audioControls.el.addEventListener('muted', this.boundHandleAudioMuted);
      this.audioControls.el.addEventListener('unmuted', this.boundHandleAudioUnmuted);

    }

    if (controlOptions.video === true) {

      this.videoControls = new VideoControls(this.videoResolution, true);

      this.videoControls.el.addEventListener('camera-stream-started', this.boundHandleCameraStarted);
      this.videoControls.el.addEventListener('camera-stream-stopped', this.boundHandleCameraStopped);

    }

    if (controlOptions.presentScreen === true) {

      this.screenPresenterControls = new ScreenPresenterControls(this.meetingSession, {
        started: this.handlePresentingStarted.bind(this),
        stopped: this.handlePresentingStopped.bind(this),
        paused: this.handlePresentingPaused.bind(this),
        unpaused: this.handlePresentingUnpaused.bind(this)
      }, meeting);

    }

    if (this.currentUserId === meeting.ownerId) {

      this.meetingAudioControls = new MeetingAudioControls(meeting);

    }

    this.attendeesControl = new AttendeesControl(this.meetingOwner);

    this.leaveMeetingControl = new LeaveMeetingControl(this.getLeaveMeetingControlTooltipMessage(), function onLeave(button) {
      meetingRoom.el.dispatchEvent(new CustomEvent('leave-meeting', { detail: { button } }));
    });

  }

  getLeaveMeetingControlTooltipMessage() {

    if (this.isOwner === true) {
      return 'End';
    }

    return 'Exit';

  }

  tryBindAudioOutput() {

    if (this.outputAudio !== true) {
      return;
    }

    this.meetingSession.audioVideo.bindAudioElement(this.speaker);

  }

  registerEventHandlers() {

    this.el.addEventListener('dblclick', this.boundHandleDoubleClickEvent);
    this.el.addEventListener('fullscreen-on', this.boundHandleFullscreenOn);
    this.el.addEventListener('fullscreen-off', this.boundHandleFullscreenOff);

    this.registerForMeetingHostVideoSettingsChangedEvent();

  }

  registerForMeetingHostVideoSettingsChangedEvent() {

    const postId = this.meeting.postId;

    this.boundHandleMeetingHostVideoSettingsChangedEvent = function handleMeetingHostVideoSettingsChangedEvent(data) {

      this.meeting = data.meeting;
      this.stage.updateScreenResolution(this.meeting);
    }.bind(this);

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_HOST_VIDEO_SETTINGS_CHANGED}:${postId}`, this.boundHandleMeetingHostVideoSettingsChangedEvent);

  }

  handleFullscreenOff() {

    this.meetingRoomVideoContentContainer.classList.remove('fullscreen');
    this.stage.draw();

  }

  handleFullscreenOn() {

    this.meetingRoomVideoContentContainer.classList.add('fullscreen');
    this.stage.draw();

  }

  handleDoubleClickEvent() {

    this.elementResizeControl.toggle();

  }

  async subscribeToAttendeeIdPresenceCallback(attendeeId, present, userId, dropped) {

    if (attendeeId.indexOf('#content') !== -1) {
      return;
    }

    if (dropped === true || present === false) {
      this.attendeesControl.removeAttendee(userId);
    } else if (present === true) {
      this.attendeesControl.addAttendee(userId);
    }

  }

  handlePresentingStarted() {

  }

  handlePresentingStopped() {

  }

  handlePresentingPaused() {

  }

  handlePresentingUnpaused() {

  }

  tryStartLocalVideoTile() {

    if (this.currentUserId !== this.meeting.ownerId) {
      return;
    }

    // this.meetingSession.audioVideo.startLocalVideoTile();

  }

  bindCameraTile(videoTileState) {

    this.meetingSession.audioVideo.bindVideoElement(videoTileState.tileId, this.meetingRoomProducerCameraDisplay);
    this.ownerVideoTileId = videoTileState.tileId;

  }

  bindScreenTile(videoTileState) {

    this.meetingSession.audioVideo.bindVideoElement(videoTileState.tileId, this.meetingRoomProducerScreenDisplay);
    this.ownerScreenVideoTileId = videoTileState.tileId;

  }

  unbindCameraTile() {

    this.ownerVideoTileId = null;

  }

  unbindScreenTile() {

    this.ownerScreenVideoTileId = null;

  }

  hasCameraTile() {

    return this.ownerVideoTileId !== null;

  }

  hasScreenTile() {

    return this.ownerScreenVideoTileId !== null;

  }

  showProducerImage() {

    this.unmountStage();
    this.mountMeetingOwnerDetails();

  }

  showStageWithCameraOnly() {

    this.unmountMeetingOwnerDetails();
    this.mountStage();
    this.stage.displayPrimaryContentAsPrimary();

  }

  showStageWithScreenOnly() {

    this.unmountMeetingOwnerDetails();
    this.mountStage();
    this.stage.displaySecondaryContentAsPrimary();

  }

  showStageWithBoth() {

    this.unmountMeetingOwnerDetails();
    this.mountStage();
    this.stage.displaySecondaryContentAsPrimary();

  }

  updateVideoDisplay() {

    const hasCameraTile = this.hasCameraTile();
    const hasScreenTile = this.hasScreenTile();

    if (hasCameraTile === false && hasScreenTile === false) {
      this.showProducerImage();
    } else if (hasCameraTile === true && hasScreenTile === false) {
      this.showStageWithCameraOnly();
    } else if (hasCameraTile === false && hasScreenTile === true) {
      this.showStageWithScreenOnly();
    } else {
      this.showStageWithBoth();
    }

  }

  muteLocal() {

    this.meetingSession.audioVideo.realtimeMuteLocalAudio();

  }

  unmuteLocal() {

    this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();

  }

  mountSpinner() {

    this.spinner = el('ht-spinner', { size: 'small' });

    mount(this.meetingRoomStageContainer, this.spinner);

  }

  unmountSpinner() {

    if (this.spinner === null) {
      return;
    }

    unmount(this.meetingRoomStageContainer, this.spinner);

  }

  mountStage() {

    mount(this.meetingRoomStageContainer, this.stage);

  }

  unmountStage() {

    unmount(this.meetingRoomStageContainer, this.stage);

  }

  mountMeetingOwnerDetails() {

    mount(this.meetingRoomStageContainer, this.userProfileDisplay);

  }

  unmountMeetingOwnerDetails() {

    unmount(this.meetingRoomStageContainer, this.userProfileDisplay);

  }

  displayHostOfflineView() {

    this.hostOfflineView = new HostOfflineView(this.onMeetingEnded.bind(this));

    mount(this.meetingRoomStageContainer, this.hostOfflineView);

  }

  hideHostOfflineView() {

    if (this.hostOfflineView !== null) {
      unmount(this.meetingRoomStageContainer, this.hostOfflineView);
    }

  }

  handleAudioMuted() {

    this.muteLocal();

  }

  async handleAudioUnmuted(event) {

    this.unmuteLocal();

  }

  async handleCameraStarted(event) {

    const { DefaultVideoTransformDevice, ConsoleLogger, LogLevel } = await import('amazon-chime-sdk-js');

    const videoInputDevices = await this.meetingSession.audioVideo.listVideoInputDevices();

    const videoHorizontalFlipProcessor = new VideoHorizontalFlipProcessor();

    await videoHorizontalFlipProcessor.load();

    const stages = [videoHorizontalFlipProcessor];
    const logger = new ConsoleLogger('HelpTapLogger', LogLevel.ERROR);
    const transformDevice = new DefaultVideoTransformDevice(logger, videoInputDevices[0].deviceId, stages);

    const stream = await this.meetingSession.audioVideo.startVideoInput(transformDevice);

    const postId = this.meeting.postId;
    const videoTrack = stream.getVideoTracks()[0];
    const videoSettings = videoTrack.getSettings();

    await serverInterface.startup.community.singleProducerMeeting.updateSingleProducerMeetingHostVideoSettingsByPostId(postId, videoSettings);

    await this.meetingSession.audioVideo.startLocalVideoTile();

  }

  handleCameraStopped() {

    this.meetingSession.audioVideo.stopLocalVideoTile();
    this.meetingSession.audioVideo.stopVideoInput();

  }

  onMeetingEnded() {
    const meeting = this.meeting;
    application.eventSystem.internal.emit(`${externalEventCodes.SINGLE_PRODUCER_MEETING_ENDED}:${meeting.postId}`, {
      meeting: {
        ...meeting,
        status: meetingStatuses.ENDED
      }
    });
  }

  mountControls() {

    const meetingRoomControls = this.meetingRoomControls;
    const controlOptions = this.controlOptions;

    if (this.currentUserId === this.meeting.ownerId) {

      mount(meetingRoomControls, this.meetingAudioControls);

    }

    if (controlOptions.audio === true) {

      mount(meetingRoomControls, this.audioControls);

    }

    if (controlOptions.video === true) {

      mount(meetingRoomControls, this.videoControls);

    }

    if (controlOptions.presentScreen === true) {

      mount(meetingRoomControls, this.screenPresenterControls);

    }

    mount(meetingRoomControls, this.attendeesControl);
    mount(meetingRoomControls, this.leaveMeetingControl);

  }

  unmountControls() {

    const meetingRoomControls = this.meetingRoomControls;
    const controlOptions = this.controlOptions;

    if (this.currentUserId === this.meeting.ownerId) {

      unmount(meetingRoomControls, this.meetingAudioControls);

    }

    if (controlOptions.audio === true) {

      unmount(meetingRoomControls, this.audioControls);

    }

    if (controlOptions.video === true) {

      unmount(meetingRoomControls, this.videoControls);

    }

    if (controlOptions.presentScreen === true) {

      unmount(meetingRoomControls, this.screenPresenterControls);

    }

    unmount(meetingRoomControls, this.attendeesControl);
    unmount(meetingRoomControls, this.leaveMeetingControl);

  }

  async releaseResources() {

    if (!this.meetingSession.audioVideo) {
      return;
    }

    await this.meetingSession.audioVideo.stopAudioInput();
    await this.meetingSession.audioVideo.stopVideoInput();
    await this.meetingSession.audioVideo.stop();

  }

}

export default MeetingRoom;